import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { Alert, AlertTitle, Divider, FormControlLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { SectionHeader } from "apps/web/src/componentLibrary/SectionHeader";
import LabeledSelect from "apps/web/src/components/universal/LabeledSelect";
import { useGoalCategoriesQuery } from "apps/web/src/types";
import { Controller, UseFormReturn } from "react-hook-form";
import { CreateGoalConfigurationTemplateDialogMode, GoalConfigurationTemplateFormType } from "../utils";
import { useTranslation } from "react-i18next";

const MAX_TASK_LENGTH = 40;
const FIELD_GAP = 2;
const SELECT_MAX_WIDTH = "240px";
const SELECT_MIN_WIDTH = "240px";

interface CreateGoalConfigurationTemplateDialogContentProps {
  form: UseFormReturn<GoalConfigurationTemplateFormType>;
  mode: CreateGoalConfigurationTemplateDialogMode;
}

export const CreateGoalConfigurationTemplateDialogContent = ({ form, mode }: CreateGoalConfigurationTemplateDialogContentProps) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    watch,
    getValues,
    setValue,
  } = form;

  const isGoalWithTarget = watch("completionCriteria.markAsComplete") === false;
  const { data } = useGoalCategoriesQuery();
  const categories = data?.goalCategories ? [...data?.goalCategories] : [];

  const renderTaskHelperText = () => (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography variant="caption" sx={{ color: theme => theme.palette.error.main }}>
        {errors.task && errors.task?.type !== "max" ? errors.task.message : " "}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: theme => (watch("task").length > MAX_TASK_LENGTH ? theme.palette.error.main : theme.palette.text.secondary),
        }}
      >
        {watch("task").length} / {MAX_TASK_LENGTH}
      </Typography>
    </Box>
  );

  const renderTypeField = () => mode !== CreateGoalConfigurationTemplateDialogMode.View ? (
      <Controller
        name={"type"}
        control={control}
        render={({ field: { ref, ...field } }) => (
          <LabeledSelect
            {...field}
            optionToName={option => `${option.defaultEmoji} ${option.name}`}
            sx={{ maxWidth: SELECT_MAX_WIDTH }}
            placeholder={"Type"}
            selectedOption={field.value ?? null}
            options={categories}
            error={Boolean(errors.type)}
            helperText={errors.type?.message}
          />
        )}
      />
    ) : (
      <TextField
        disabled={true}
        sx={{ minWidth: SELECT_MIN_WIDTH }}
        value={`${getValues("type.defaultEmoji")} ${getValues("type.name")}`}
      />
  );

  const renderInfoAlert = () => (
    <Alert severity="info" icon={<InfoRoundedIcon />} sx={{ mb: 2 }}>
      <AlertTitle sx={{ lineHeight: "20px"}}>View goal</AlertTitle>
      <Typography variant="body2">
        {t("goalConfigurationTemplates.bulkAssign.dialog.createGoalConfigurationTemplate.infoAlert")}
      </Typography>
    </Alert>
  );

  return (
    <>
      <Box>
        {mode === CreateGoalConfigurationTemplateDialogMode.View && renderInfoAlert()}
        <SectionHeader
          header={t("goalConfigurationTemplates.bulkAssign.dialog.createGoalConfigurationTemplate.goalDetailsHeader")}
          subcopy={t("goalConfigurationTemplates.bulkAssign.dialog.createGoalConfigurationTemplate.goalDetailsSubcopy")}
        />
      </Box>
      <Box sx={{ display: "flex", gap: FIELD_GAP }}>
        <Controller
          name={"task"}
          control={control}
          render={({ field: { ref, ...field } }) => (
            <TextField
              {...field}
              placeholder="e.g., Drink 3 liters of water"
              error={Boolean(errors.task)}
              helperText={renderTaskHelperText()}
              label="Task"
              fullWidth
              disabled={mode === CreateGoalConfigurationTemplateDialogMode.View}
            />
          )}
        />
        {renderTypeField()}
      </Box>
      <Divider />
      <SectionHeader
        header={t("goalConfigurationTemplates.bulkAssign.dialog.createGoalConfigurationTemplate.completionCriteriaHeader")}
        subcopy={t("goalConfigurationTemplates.bulkAssign.dialog.createGoalConfigurationTemplate.completionCriteriaSubcopy")}
      />
      <Box sx={{ display: "flex", gap: FIELD_GAP }}>
        <Controller
          name={"completionCriteria.markAsComplete"}
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {errors.completionCriteria?.markAsComplete && (
                <Typography variant="body2" sx={{ pb: 2, color: theme => theme.palette.error.main }}>
                  {errors.completionCriteria?.markAsComplete?.message}
                </Typography>
              )}
              <RadioGroup value={field.value}>
                <FormControlLabel
                  label={
                    <>
                      <Typography>Mark as complete</Typography>
                      <Typography
                        variant="body1"
                        sx={{ pb: 2, fontSize: "14px", lineHeight: "18px", color: theme => theme.palette.text.secondary }}
                      >
                        Check yes or no (e.g., eat a snack before lift)
                      </Typography>
                    </>
                  }
                  control={
                    <Radio
                      {...field}
                      sx={{ pb: 3 }}
                      value={true}
                      disabled={mode === CreateGoalConfigurationTemplateDialogMode.View}
                      onChange={() => {
                        field.onChange(true);
                        setValue("completionCriteria.target", undefined);
                      }}
                    />
                  }
                />
                <FormControlLabel
                  label={
                    <>
                      <Typography>Update progress</Typography>
                      <Typography
                        variant="body1"
                        sx={{ pb: 2, fontSize: "14px", lineHeight: "18px", color: theme => theme.palette.text.secondary }}
                      >
                        Specify the quantity (e.g., drink 3 liters of water)
                      </Typography>
                    </>
                  }
                  labelPlacement="end"
                  control={
                    <Radio
                      disabled={mode === CreateGoalConfigurationTemplateDialogMode.View}
                      sx={{ pb: 3 }}
                      value={false}
                      checked={field.value === false}
                      onChange={() => field.onChange(false)}
                    />
                  }
                />
              </RadioGroup>
            </Box>
          )}
        />
      </Box>
      {isGoalWithTarget && (
        <Box sx={{ display: "flex", gap: FIELD_GAP, pl: 4 }}>
          <Controller
            name={"completionCriteria.target.value"}
            control={control}
            render={({ field: { ref, onChange, ...field }, fieldState }) => (
              <TextField
                {...field}
                onChange={v => onChange(parseInt(v.target.value))}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                label="Quantity"
                placeholder="#"
                type="number"
                disabled={mode === CreateGoalConfigurationTemplateDialogMode.View}
              />
            )}
          />
          <Controller
            name={"completionCriteria.target.unit"}
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <TextField
                {...field}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                label="Unit"
                placeholder="e.g. liters"
                disabled={mode === CreateGoalConfigurationTemplateDialogMode.View}
              />
            )}
          />
        </Box>
      )}
    </>
  );
};
